// Import react elements
import { useEffect } from "react";

// Import global hooks
import useFormHandler from "global/hooks/useFormHandler";
import { useToast } from "global/hooks/useToast";

// Import hooks
import useCustomProductsData from "../../hooks/useCustomProductsData";

// Import components
import CreateButton from "global/components/CreateButton";
import TwoColumnList from "global/components/TwoColumnList";

// Import utility functions
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";



// Render surface details inside the custom product editor
const RenderSurfaceDetails = ({
    selectedSurfaceData,
    updateExistingSurface,
    selectedCustomProduct,
    setSelectedCustomProduct,
    setSelectedSurfaceData,
    renderSurfacePrintAreas,
    onDirtyChange,
}) => {

    // Define toast options - successful changes are reflected directly in the UI
    const { toastError } = useToast();

    // Custom hook for fetching and managing product data
    const {
        deleteSurface
    } = useCustomProductsData();

    // Get the surface details because we don't want the index in the form
    const surfaceDetails = selectedSurfaceData.details;


    // Use the form handler for this single object
    const {
        formValues,
        handleChange,
        isFormPartlyEdited,
    } = useFormHandler({
        initialValues: surfaceDetails,
        syncInitialValues: true,
        convertBooleans: true,
        convertNumbers: true,
        validate: (values) => {
        }
    });

    // Work out whether changing tree node would discard local surface changes
    const surfaceHasUnsavedChanges = isFormPartlyEdited();

    // Tell the parent popup whether this nested editor is currently dirty
    useEffect(() => {

        onDirtyChange(surfaceHasUnsavedChanges);

        // Clear the dirty flag when this surface editor unmounts
        return () => onDirtyChange(false);

    }, [surfaceHasUnsavedChanges, onDirtyChange]);


    // Handle local form submit
    const handleSubmitSaveSurface = async (event) => {

        // Prevent default browser submit behaviour
        event.preventDefault();

        // Save the edited surface through the parent data helper
        const response = await updateExistingSurface(
            selectedCustomProduct.code,
            selectedSurfaceData.details.code,
            formValues,
            selectedCustomProduct.id,
            setSelectedCustomProduct
        );

        // If parent returns a success flag, make these values the new local baseline
        if (response.success) {

            setSelectedSurfaceData({details: formValues, index: selectedSurfaceData.index});
            onDirtyChange(false);

        } else {

            // Only failures need a toast
            toastError({ title: "Failed", message: "Could not update surface: " + response.error_message });

        }

    };


    // Function to handle clicking delete surface
    const handleDeleteSurface = async () => {

        DisplayConfirmationDialog(
            "Are you sure you want to delete this surface: " + formValues.name + "?",
            "Delete",
            "Cancel",
            async function (is_confirmed) {

                if (is_confirmed) {

                    const response = await deleteSurface(
                        selectedCustomProduct.code,
                        selectedSurfaceData.details.code,
                        setSelectedCustomProduct
                    );

                    if (response.success) {

                        // Return the Surfaces branch to its root after deletion
                        onDirtyChange(false);
                        setSelectedSurfaceData(null);

                    } else {

                        toastError({ title: "Error", message: "Could not delete surface: " + response.error_message });

                    }

                }

            }
        );

    };


    return (

        <div className="custom-product-editor__panel">

            <div className="custom-product-editor__section-heading">
                <h3>Surface: {formValues.name || surfaceDetails.code}</h3>
            </div>

            <form id="surfaceDetailsForm" onSubmit={handleSubmitSaveSurface}>

                <TwoColumnList
                    property={<label>Name</label>}
                    value={<input
                        name="name"
                        type="text"
                        placeholder="Name"
                        value={formValues.name || ""}
                        onChange={handleChange}
                        required
                    />}
                />

                <TwoColumnList
                    property={<label>Code</label>}
                    value={<input
                        name="code"
                        type="text"
                        placeholder="Code"
                        value={formValues.code || ""}
                        onChange={handleChange}
                        required
                    />}
                />

                <TwoColumnList
                    property={<label>Description</label>}
                    value={<textarea name="description" rows="1" cols="30" value={formValues.description || ""} onChange={handleChange} placeholder="Description"></textarea>}
                />

            </form>


            {/* Print Areas are the only child collection on a Surface, so they do not need a tab */}
            <div className="custom-product-editor__subsection-heading">
                <h4>Print Areas</h4>
            </div>

            {renderSurfacePrintAreas()}


            {/* Surface actions stay with the Surface editor; navigation is handled by the tree */}
            <div className="custom-product-editor__section-actions horizontal-align-content-center flex-justify-left-right">

                <CreateButton
                    title="Delete Surface"
                    isDelete={true}
                    tooltip="Delete Surface"
                    size="m"
                    icon="/images/icon_bin.png"
                    onClick={() => handleDeleteSurface()}
                />

                {surfaceHasUnsavedChanges &&

                    <CreateButton
                        type="submit"
                        title="Save changes"
                        size="m"
                        formID="surfaceDetailsForm"
                    />

                }

            </div>

        </div>

    );

}; export default RenderSurfaceDetails;
